import { formatCurrencyCompact, formatPercentage } from '@/utils/formatters';
import { WaterfallSummaryCard } from './WaterfallSummaryCard';
import type { WaterfallMetric } from './WaterfallContent';
import type { ParsedDistributionWaterfallResult } from '@/hooks/react-query/useDistributionWaterfall';

interface WaterfallMetricsGridProps {
  overall: ParsedDistributionWaterfallResult;
  currency: string;
  isTheoretical: boolean;
  selectedMetric: WaterfallMetric;
  onCardClick: (metric: WaterfallMetric) => void;
  totalLp: number;
  totalGp: number;
  totalProfit: number;
}

export function WaterfallMetricsGrid({
  overall,
  currency,
  isTheoretical,
  selectedMetric,
  onCardClick,
  totalLp,
  totalGp,
  totalProfit,
}: WaterfallMetricsGridProps) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
        <WaterfallSummaryCard
          title="Total Contributed"
          amount={overall.called_capital}
          currency={currency}
          subtitle="Total capital called from LPs"
          isSelected={selectedMetric === 'called_capital'}
          onClick={() => onCardClick('called_capital')}
        />
        <WaterfallSummaryCard
          title={
            isTheoretical ? 'Total Value (NAV + Dist.)' : 'Total Distributions'
          }
          amount={overall.total_distribution_amount}
          currency={currency}
          subtitle={
            isTheoretical
              ? 'Current NAV + Historic Distributions'
              : 'Actual distributions processed'
          }
          isSelected={selectedMetric === 'total_distribution_amount'}
          onClick={() => onCardClick('total_distribution_amount')}
        />
        <WaterfallSummaryCard
          title="Accrued Hurdle"
          amount={overall.preferred_return_amount}
          currency={currency}
          subtitle={`${formatPercentage(overall.hurdle_rate, 0)} time-weighted preferred return`}
          className="bg-muted/30"
          isSelected={selectedMetric === 'preferred_return_amount'}
          onClick={() => onCardClick('preferred_return_amount')}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <WaterfallSummaryCard
          title={
            isTheoretical
              ? 'Cumulative Theo. LP Share'
              : 'Total LP Distributions'
          }
          amount={totalLp}
          currency={currency}
          subtitle={isTheoretical ? 'Including past distributions' : ''}
          isSelected={selectedMetric === 'total_lp'}
          onClick={() => onCardClick('total_lp')}
        />
        <WaterfallSummaryCard
          title={
            isTheoretical ? 'Cumulative Theo. Carry' : 'Total Carried Interest'
          }
          amount={totalGp}
          currency={currency}
          subtitle={isTheoretical ? 'Including past distributions' : ''}
          isSelected={selectedMetric === 'total_gp'}
          onClick={() => onCardClick('total_gp')}
        />
        <WaterfallSummaryCard
          title={
            isTheoretical ? 'Cumulative Theo. Profit' : 'Net Realized Profit'
          }
          amount={totalProfit}
          subtitle={`Accrued Hurdle: ${formatCurrencyCompact(overall.preferred_return_amount, currency)}`}
          currency={currency}
          isSelected={selectedMetric === 'total_profit'}
          onClick={() => onCardClick('total_profit')}
        />
      </div>
    </>
  );
}
